import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI } from '../utils/api';
import { toast } from 'sonner';
import { User, Phone, MapPin, AlertCircle, Award, Coins } from 'lucide-react';
import { formatCurrency } from '../utils/currency';

const Profile = () => {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Only admins can edit profiles (including their own)
  const canEditProfile = user?.role === 'admin';
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    employee_id: '',
    id_number: '',
    address: '',
    emergency_contact: '',
    emergency_contact_phone: '',
    skills: [],
    certifications: [],
    hourly_rate: 0,
    job_description: '',
    bibc_job_code: '',
  });
  const [skillInput, setSkillInput] = useState('');
  const [certInput, setCertInput] = useState('');

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        phone: user.phone || '',
        employee_id: user.employee_id || '',
        id_number: user.id_number || '',
        address: user.address || '',
        emergency_contact: user.emergency_contact || '',
        emergency_contact_phone: user.emergency_contact_phone || '',
        skills: user.skills || [],
        certifications: user.certifications || [],
        hourly_rate: user.hourly_rate || 0,
        job_description: user.job_description || '',
        bibc_job_code: user.bibc_job_code || '',
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await usersAPI.update(user.id, formData);
      toast.success('Profile updated successfully');
      setEditing(false);
      // Refresh user data
      window.location.reload();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const addSkill = () => {
    if (skillInput.trim() && !formData.skills.includes(skillInput.trim())) {
      setFormData({ ...formData, skills: [...formData.skills, skillInput.trim()] });
      setSkillInput('');
    }
  };

  const removeSkill = (skill) => {
    setFormData({ ...formData, skills: formData.skills.filter((s) => s !== skill) });
  };

  const addCertification = () => {
    if (certInput.trim() && !formData.certifications.includes(certInput.trim())) {
      setFormData({ ...formData, certifications: [...formData.certifications, certInput.trim()] });
      setCertInput('');
    }
  };

  const removeCertification = (cert) => {
    setFormData({ ...formData, certifications: formData.certifications.filter((c) => c !== cert) });
  };

  return (
    <div data-testid="profile-page" className="p-6 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          My Profile
        </h1>
        <p className="text-slate-600">{user?.role}</p>
      </div>

      <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-3xl font-bold text-white">{user?.name?.charAt(0).toUpperCase()}</span>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {user?.name}
              </h2>
              <p className="text-slate-600">{user?.email}</p>
            </div>
          </div>
          {!editing && canEditProfile && (
            <button
              data-testid="edit-profile-button"
              onClick={() => setEditing(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg shadow-sm transition-colors"
            >
              Edit Profile
            </button>
          )}
          {!canEditProfile && (
            <div className="text-sm text-slate-500 bg-slate-100 px-4 py-2 rounded-lg">
              Contact an administrator to update your profile
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <User className="w-4 h-4 inline mr-2" />
                Full Name
              </label>
              <input
                data-testid="name-input"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                disabled={!editing}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all disabled:bg-slate-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <Phone className="w-4 h-4 inline mr-2" />
                Phone Number
              </label>
              <input
                data-testid="phone-input"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                disabled={!editing}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all disabled:bg-slate-50"
                placeholder="+27 12 345 6789"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Employee ID</label>
              <input
                data-testid="employee-id-input"
                type="text"
                value={formData.employee_id}
                onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                disabled={!editing}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all disabled:bg-slate-50"
                placeholder="EMP-001"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">ID Number</label>
              <input
                data-testid="id-number-input"
                type="text"
                value={formData.id_number}
                onChange={(e) => setFormData({ ...formData, id_number: e.target.value })}
                disabled={!editing}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all disabled:bg-slate-50"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-2" />
                Address
              </label>
              <textarea
                data-testid="address-input"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                disabled={!editing}
                rows={2}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all disabled:bg-slate-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <AlertCircle className="w-4 h-4 inline mr-2" />
                Emergency Contact Name
              </label>
              <input
                data-testid="emergency-contact-input"
                type="text"
                value={formData.emergency_contact}
                onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                disabled={!editing}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all disabled:bg-slate-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Emergency Contact Phone</label>
              <input
                data-testid="emergency-phone-input"
                type="tel"
                value={formData.emergency_contact_phone}
                onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
                disabled={!editing}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all disabled:bg-slate-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <Coins className="w-4 h-4 inline mr-2" />
                Wage Rate per Hour (R)
              </label>
              <input
                data-testid="hourly-rate-input"
                type="text"
                value={formData.hourly_rate}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9.]/g, '');
                  setFormData({ ...formData, hourly_rate: value ? parseFloat(value) : 0 });
                }}
                disabled={!editing}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all disabled:bg-slate-50"
              />
              {!editing && formData.hourly_rate > 0 && (
                <p className="text-sm text-slate-600 mt-1">{formatCurrency(formData.hourly_rate)}/hour</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">BIBC Job Code</label>
              <input
                data-testid="bibc-code-input"
                type="text"
                value={formData.bibc_job_code}
                onChange={(e) => setFormData({ ...formData, bibc_job_code: e.target.value })}
                disabled={!editing}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all disabled:bg-slate-50"
                placeholder="e.g., BIBC-2024-001"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Job Description</label>
            <textarea
              data-testid="job-description-input"
              value={formData.job_description}
              onChange={(e) => setFormData({ ...formData, job_description: e.target.value })}
              disabled={!editing}
              rows={3}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all disabled:bg-slate-50"
              placeholder="Describe the job role and responsibilities..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              <Award className="w-4 h-4 inline mr-2" />
              Skills
            </label>
            {editing && (
              <div className="flex gap-2 mb-2">
                <input
                  data-testid="skill-input"
                  type="text"
                  value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  placeholder="Add a skill..."
                />
                <button
                  type="button"
                  onClick={addSkill}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Add
                </button>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {formData.skills.map((skill, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                >
                  {skill}
                  {editing && (
                    <button type="button" onClick={() => removeSkill(skill)} className="hover:text-blue-900">
                      ×
                    </button>
                  )}
                </span>
              ))}
              {formData.skills.length === 0 && <p className="text-slate-500 text-sm">No skills added</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Certifications</label>
            {editing && (
              <div className="flex gap-2 mb-2">
                <input
                  data-testid="cert-input"
                  type="text"
                  value={certInput}
                  onChange={(e) => setCertInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCertification())}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  placeholder="Add a certification..."
                />
                <button
                  type="button"
                  onClick={addCertification}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Add
                </button>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {formData.certifications.map((cert, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                >
                  {cert}
                  {editing && (
                    <button type="button" onClick={() => removeCertification(cert)} className="hover:text-green-900">
                      ×
                    </button>
                  )}
                </span>
              ))}
              {formData.certifications.length === 0 && (
                <p className="text-slate-500 text-sm">No certifications added</p>
              )}
            </div>
          </div>

          {editing && (
            <div className="flex gap-3 pt-4 border-t border-slate-200">
              <button
                data-testid="save-profile-button"
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg shadow-sm transition-colors disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                data-testid="cancel-edit-button"
                type="button"
                onClick={() => setEditing(false)}
                className="px-6 py-3 border border-slate-200 text-slate-700 hover:bg-slate-50 font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default Profile;
